/**
 * The row window, as React sees it.
 *
 * One store per view, created with the component and destroyed with it. The
 * store itself knows nothing about React -- that is what lets its tests drive it
 * with a fake clock -- so this is the whole of the binding: create it, keep it
 * pointed at the current query and the measured viewport, and tear it down.
 */
import { useEffect, useRef, useState } from 'react'
import { useStore } from 'zustand'

import type { Field } from '@/api/types'
import { fetchViewRows, type RowFetcher } from '../rows/rowFetcher'
import { registerRowWindow, type RowWindowMember } from '../rows/rowWindowRegistry'
import { rowQuery, type QueryableView, type RowSearch } from '../rows/rowQuery'
import {
  createRowWindowStore,
  type RowWindowState,
  type RowWindowStore,
} from '../rows/rowWindowStore'

/**
 * Creates the store for one grid and tears it down with it.
 *
 * The fetcher is a parameter so a publicly shared view can use the same engine
 * against its own endpoint: everything above the fetcher -- the window algebra,
 * the scroll policy, the staleness rule -- is the same question asked of a
 * different address.
 *
 * One store per mounted grid, and no handling of the view changing underneath
 * it: the caller keys the component on the view id, which is React's way of
 * saying "this is a different thing now" and gets the store rebuilt for free. A
 * *query* change is not that -- a filter is the same window asking a different
 * question, and rebuilding for it would throw away the scroll position.
 */
export function useRowWindowStore(fetchRows: RowFetcher = fetchViewRows): RowWindowStore {
  // Read once, on the first render: a fetcher changing under a live window would
  // mean the answers in flight came from somewhere other than the one that will
  // handle them. A public grid is keyed on its slug, so a different view is a
  // different component and gets a different store.
  const [store] = useState<RowWindowStore>(() => createRowWindowStore({ fetchRows }))

  // StrictMode runs this cleanup and then mounts again, so `destroy` has to be
  // survivable; `setQuery` revives the store on the way back in.
  useEffect(() => () => store.getState().destroy(), [store])

  return store
}

/**
 * Points a store at what the view is currently asking for.
 *
 * The query is rebuilt on every render because the view object is, and the store
 * compares signatures rather than references -- so a background refetch of the
 * view costs one string comparison rather than a reload of the rows.
 */
export function useRowQuery(
  store: RowWindowStore,
  view: QueryableView,
  search: RowSearch,
): void {
  const query = rowQuery(view, search)

  // Depends on the signature, not on the query: the object around it is rebuilt
  // on every render and reacting to that would reset the window on every render.
  const { signature } = query
  useEffect(() => {
    store.getState().setQuery(query)
  }, [store, signature])
}

/** Subscribes to one slice of the window. */
export function useRowWindow<T>(
  store: RowWindowStore,
  select: (state: RowWindowState) => T,
): T {
  return useStore(store, select)
}

/**
 * Makes a mounted window reachable by the realtime layer.
 *
 * The socket is created outside React and row events name a table, so a window
 * that never announces itself is one nothing can tell about a row somebody else
 * created. Registered for as long as the grid is mounted, and no longer.
 *
 * The fields and the view are read through a ref rather than captured, because
 * both change under a mounted grid: a decision about whether a row matches,
 * taken against the filters as they were when the page loaded, is a decision
 * about a view that is not on screen.
 */
export function useRegisteredRowWindow(
  store: RowWindowStore,
  {
    tableId,
    viewId,
    fields,
    view,
  }: {
    tableId: number
    viewId: number
    fields: readonly Field[]
    view: QueryableView
  },
): RowWindowMember {
  const latest = useRef({ fields, view })
  latest.current = { fields, view }

  // Built once and handed back, so this tab's own edits describe the window the
  // same way a realtime event does. The member is stable across renders because
  // everything that changes is read through the ref inside it.
  const [member] = useState<RowWindowMember>(() => ({
    tableId,
    viewId,
    store,
    fields: () => latest.current.fields,
    view: () => latest.current.view,
    groupBys: () => latest.current.view.group_bys ?? [],
  }))

  useEffect(() => registerRowWindow(member), [member])

  return member
}
